import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize.tsx';
import { PopoverTriggerButton } from '@openx/components-core/lib/Button/PopoverTriggerButton.tsx';
import { FiltersPopover } from '@openx/components-core/lib/FiltersPopover/FiltersPopover.tsx';
import {
  type DealInsightsFilterEntryApi,
  type DealInsightsFilterOptionKeys,
  type DealInsightsMoreFiltersOptions,
  dealInsightsFilterOptionKeys,
} from '@openx/types/dealInsights.ts';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { dealInsightsStateFields } from '../../../constants.ts';
import { useDealInsightsContext } from '../../../context/DealInsightsContext.tsx';
import type { DealInsightsState } from '../../../types.ts';
import { getMoreFiltersLabel } from '../../../utils/getMoreFiltersLabel.ts';
import { mapFilterOptionLabel } from '../../../utils/mapFilterOptionLabel.ts';

type DealInsightsMoreFiltersProps = {
  filterOptions: DealInsightsMoreFiltersOptions;
  loading: boolean;
};

export const DealInsightsMoreFilters = ({ filterOptions, loading }: DealInsightsMoreFiltersProps) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const { filters: contextFilters, setFilters: setContextFilters } = useDealInsightsContext();
  const { setValue, watch, getValues } = useFormContext();

  const observableLastOpenedFilterKey = watch(dealInsightsStateFields.LAST_OPENED_FILTER_KEY);

  const initialFilters = useMemo(() => {
    return getValues(dealInsightsStateFields.FILTERS);
  }, [getValues]);

  const observableFormValue = watch(dealInsightsStateFields.FILTERS);
  const hasChanges = JSON.stringify(observableFormValue) !== JSON.stringify(contextFilters);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setValue(dealInsightsStateFields.FILTERS, contextFilters);
    setAnchorEl(null);
  }, [contextFilters, setValue]);

  const handleApply = useCallback(() => {
    const { deal: _deal, ...formValueWithoutDeal } = observableFormValue;

    setContextFilters({ deal: contextFilters.deal, ...formValueWithoutDeal });
    setAnchorEl(null);
  }, [observableFormValue, setContextFilters, contextFilters]);

  const handleClear = useCallback(() => {
    const { deal: _deal, ...initialFiltersWithoutDeal } = initialFilters;

    const revertedFilters = { ...initialFiltersWithoutDeal, deal: contextFilters.deal };

    setValue(dealInsightsStateFields.FILTERS, revertedFilters);
    setContextFilters(revertedFilters);

    setAnchorEl(null);
  }, [setValue, setContextFilters, initialFilters, contextFilters]);

  const isContextEmpty = useMemo(() => {
    return !Object.keys(contextFilters).some(key => contextFilters[key].length > 0);
  }, [contextFilters]);

  const contextFiltersCount = useMemo(() => {
    let count = 0;

    Object.keys(contextFilters).forEach(key => {
      if (key !== dealInsightsFilterOptionKeys.DEAL) {
        count += contextFilters[key].length;
      }
    });

    return count;
  }, [contextFilters]);

  const isFormEmpty = useCallback((formValue: DealInsightsState['filters']) => {
    return !Object.keys(formValue).some(key => formValue[key].length > 0);
  }, []);

  const isApplyable = useMemo(() => {
    return hasChanges;
  }, [hasChanges]);

  const buttonText = useMemo(() => {
    const base = t('More filters');

    if (contextFiltersCount === 0) {
      return base;
    }

    return `${base} +${contextFiltersCount}`;
  }, [contextFiltersCount, t]);

  return (
    <>
      <PopoverTriggerButton
        data-test={'more-filters-button'}
        onClick={handleOpen}
        isEmpty={isContextEmpty}
        isOpen={isOpen}
      >
        {buttonText}
      </PopoverTriggerButton>

      <FiltersPopover
        data-test={'more-filters-popover'}
        open={isOpen}
        onClose={handleClose}
        onApply={handleApply}
        onClear={handleClear}
        title={t('Filters')}
        anchorEl={anchorEl}
        slotProps={{ paper: { sx: { width: '360px' } } }}
        isApplyable={isApplyable}
        isClearable={!isFormEmpty(observableFormValue)}
      >
        {Object.keys(filterOptions).map(filterKey => (
          <RHFAutoCompleteVirtualize
            key={filterKey}
            loading={loading && observableLastOpenedFilterKey !== filterKey}
            disabled={loading && observableLastOpenedFilterKey !== filterKey}
            onOpen={() => setValue(dealInsightsStateFields.LAST_OPENED_FILTER_KEY, filterKey)}
            name={`${dealInsightsStateFields.FILTERS}.${filterKey}`}
            textFieldProps={{
              label: getMoreFiltersLabel(t, filterKey as DealInsightsFilterOptionKeys),
              margin: 'dense',
              placeholder: t('Select or search'),
            }}
            sx={{
              '& .MuiAutocomplete-inputRoot': {
                maxHeight: '200px',
                overflowY: 'auto',
              },
            }}
            multiple
            fullWidth
            options={filterOptions[filterKey] as DealInsightsFilterEntryApi[]}
            getOptionLabel={option => mapFilterOptionLabel(option, filterKey as DealInsightsFilterOptionKeys, t)}
            getOptionKey={option => option.name ?? ''}
            data-test={`more-filters-autocomplete-${filterKey}`}
            isOptionEqualToValue={(option, value) => {
              if (typeof value === 'string') return false;
              return option.name === value.name;
            }}
          />
        ))}
      </FiltersPopover>
    </>
  );
};
